/**
 * @file        src/pages/flight/UpdateBookingModal.jsx
 * @description DB-only admin update for a flight booking, matching the legacy
 *              "Update Ticket" form: OB Status (Pending / Success / Failed /
 *              Hold), PNR, a per-passenger Ticket Number field, and a Remark.
 *              Does not call the airline — it records the operational state we
 *              hold. OB Status labels map to the flight_bookings ENUM.
 * @author      Kinetika Labs
 * @created     2026-07-09
 * @changelog
 *   - 2026-07-09 Claude — Initial (Booking List row actions).
 *   - 2026-07-09 Claude — OB Status labels + per-passenger ticket numbers.
 *   - 2026-08-10 Claude — OB Status now uses the themed Select (console-wide
 *                          dropdown consistency pass) instead of a native <select>.
 *   - 2026-09-09 Claude — Ticket numbers are validated before submit (lib/
 *                          ticketNumber, mirroring the server rule). The field
 *                          accepted anything, so a 2-digit typo was saved onto
 *                          every passenger row and printed on the e-ticket.
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 *   - 2026-09-15 Claude — Remark input capped at 500 chars, matching the route schema.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import { updateBooking, getBookingDetails } from '../../api/flightBookings';
import { isValidTicketNumber, TICKET_NUMBER_MESSAGE } from '../../lib/ticketNumber';
import { apiErrorMessage } from '../../api/client';

// Legacy "OB Status" labels → the flight_bookings.booking_status ENUM values.
const OB_STATUS = [
  { label: 'Pending', value: 'Pending' },
  { label: 'Success', value: 'Booked' },
  { label: 'Failed', value: 'Failed' },
  { label: 'Hold', value: 'Awaiting confirmation' },
];
const toOb = (enumVal) => {
  const s = String(enumVal || '');
  if (/booked/i.test(s)) return 'Booked';
  if (/fail/i.test(s)) return 'Failed';
  if (/await|hold/i.test(s)) return 'Awaiting confirmation';
  return 'Pending';
};
const paxName = (p, i) =>
  p.name
  || [p.title, p.firstName, p.lastName].filter(Boolean).join(' ')
  || p.full_name || `Passenger ${i + 1}`;

export default function UpdateBookingModal({ booking, onClose }) {
  const qc = useQueryClient();

  // Load passengers so we can show a ticket-number field per traveller.
  const { data: det } = useQuery({
    queryKey: ['flight-booking-details', booking.id],
    queryFn: () => getBookingDetails(booking.id),
  });
  const d = det?.data;
  const passengers = (d?.passengers && d.passengers.length ? d.passengers : (d?.ticketed_pax || []));

  const [status, setStatus] = useState(toOb(booking.booking_status));
  const [pnr, setPnr] = useState(booking.pnr_code || '');
  const [remark, setRemark] = useState('');
  const [tickets, setTickets] = useState({}); // index → ticket number
  const [err, setErr] = useState('');
  const [ticketErrs, setTicketErrs] = useState({}); // index → message

  // A blank field means "leave this passenger's ticket alone"; anything else
  // must be a real ticket number. Returns the errors so submit can use them
  // without waiting for a state update.
  const validateTickets = () => {
    const next = {};
    Object.entries(tickets).forEach(([i, v]) => {
      if (String(v || '').trim() && !isValidTicketNumber(v)) next[i] = TICKET_NUMBER_MESSAGE;
    });
    setTicketErrs(next);
    return next;
  };

  const mut = useMutation({
    mutationFn: () => {
      const count = passengers.length || 1;
      const arr = Array.from({ length: count }, (_, i) => tickets[i] ?? '');
      return updateBooking(booking.id, {
        booking_status: status,
        pnr_code: pnr,
        remark,
        tickets: arr.some(Boolean) ? arr : undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flight-bookings'] });
      qc.invalidateQueries({ queryKey: ['flight-cancellations'] });
      qc.invalidateQueries({ queryKey: ['flight-booking-details', booking.id] });
      // The drawer's Timeline card — this update just appended an event to it.
      qc.invalidateQueries({ queryKey: ['booking-logs'] });
      onClose();
    },
    onError: (e) => setErr(apiErrorMessage(e, 'Update failed.')),
  });

  const setTk = (i) => (e) => {
    setTickets({ ...tickets, [i]: e.target.value });
    if (ticketErrs[i]) setTicketErrs({ ...ticketErrs, [i]: undefined });
  };

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 620 }}>
        <div className="lk-modal-head">
          <h3>Update Ticket · #{booking.id}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>
        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            setErr('');
            if (Object.keys(validateTickets()).length) {
              setErr('Fix the ticket number(s) highlighted below.');
              return;
            }
            mut.mutate();
          }}
        >
          <div className="field">
            <label>OB Status</label>
            <Select value={status} onChange={setStatus} options={OB_STATUS} />
          </div>
          <div className="field">
            <label>PNR</label>
            <input value={pnr} onChange={(e) => setPnr(e.target.value)} placeholder="Airline PNR" />
          </div>
          <div className="field">
            <label>Remark</label>
            <input value={remark} maxLength={500} onChange={(e) => setRemark(e.target.value)} placeholder="Reason / note (audit trail)" />
          </div>

          {passengers.length ? passengers.map((p, i) => (
            <div className="field full" key={i}>
              <label>{paxName(p, i)} — Ticket Number</label>
              <input
                value={tickets[i] ?? p.ticketNumber ?? p.ticket_number ?? ''}
                onChange={setTk(i)}
                onBlur={validateTickets}
                placeholder="e.g. 098-1234567890"
                aria-invalid={ticketErrs[i] ? 'true' : undefined}
              />
              {ticketErrs[i] ? <div className="field-error">{ticketErrs[i]}</div> : null}
            </div>
          )) : (
            <div className="field full">
              <label>Ticket Number</label>
              <input
                value={tickets[0] ?? ''}
                onChange={setTk(0)}
                onBlur={validateTickets}
                placeholder="e.g. 098-1234567890"
                aria-invalid={ticketErrs[0] ? 'true' : undefined}
              />
              {ticketErrs[0] ? <div className="field-error">{ticketErrs[0]}</div> : null}
            </div>
          )}

          {err ? <div className="field-error full">{err}</div> : null}
          <div className="form-actions full">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              {mut.isPending ? 'Saving…' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
